/**
 * Sampler landing — procedural "packaging" sky (placeholder until a real cloud photo is picked)
 * Paints grainy, top-lit cumulus into every <canvas data-smp-clouds>. Deterministic (seeded),
 * computed at 600px wide and scaled by CSS; a fine grain tile is layered on top in CSS.
 * Only used when the section's "Sky background" image setting is empty — pick an image to replace it.
 */
(function () {
  function rng(seed) {
    var s = seed >>> 0;
    return function () {
      s += 0x6D2B79F5;
      var t = s;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function normal(rand) {
    var u = 1 - rand(), v = rand();
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  }

  // Value noise on a random grid (cell sx wide, sy tall), smoothstep-interpolated
  function octave(W, H, sx, sy, rand) {
    var gx = Math.ceil(W / sx) + 2, gy = Math.ceil(H / sy) + 2;
    var g = new Float32Array(gx * gy);
    for (var i = 0; i < g.length; i++) g[i] = rand();
    var out = new Float32Array(W * H);
    for (var y = 0; y < H; y++) {
      var fy = y / sy, iy = fy | 0, ty = fy - iy; ty = ty * ty * (3 - 2 * ty);
      for (var x = 0; x < W; x++) {
        var fx = x / sx, ix = fx | 0, tx = fx - ix; tx = tx * tx * (3 - 2 * tx);
        var a = g[iy * gx + ix], b = g[iy * gx + ix + 1];
        var c = g[(iy + 1) * gx + ix], d = g[(iy + 1) * gx + ix + 1];
        var top = a + (b - a) * tx, bot = c + (d - c) * tx;
        out[y * W + x] = top + (bot - top) * ty;
      }
    }
    return out;
  }
  function fbm(W, H, scales, falloff, S, rand) {
    var out = new Float32Array(W * H), amp = 1, tot = 0;
    scales.forEach(function (s) {
      var n = octave(W, H, Math.max(1.5, s * S * 1.3), Math.max(1.5, s * S), rand);
      for (var i = 0; i < out.length; i++) out[i] += n[i] * amp;
      tot += amp; amp *= falloff;
    });
    for (var j = 0; j < out.length; j++) out[j] /= tot;
    return out;
  }

  // Separable box blur x3 ≈ gaussian
  function blur(src, W, H, sigma) {
    var r = Math.max(1, Math.round(sigma * 0.87));
    var a = new Float32Array(src), b = new Float32Array(src.length);
    for (var pass = 0; pass < 3; pass++) {
      for (var y = 0; y < H; y++) {
        var acc = 0, row = y * W;
        for (var x = -r; x <= r; x++) acc += a[row + Math.min(W - 1, Math.max(0, x))];
        for (var x2 = 0; x2 < W; x2++) {
          b[row + x2] = acc / (2 * r + 1);
          acc += a[row + Math.min(W - 1, x2 + r + 1)] - a[row + Math.max(0, x2 - r)];
        }
      }
      for (var x3 = 0; x3 < W; x3++) {
        var acc2 = 0;
        for (var yy = -r; yy <= r; yy++) acc2 += b[Math.min(H - 1, Math.max(0, yy)) * W + x3];
        for (var y2 = 0; y2 < H; y2++) {
          a[y2 * W + x3] = acc2 / (2 * r + 1);
          acc2 += b[Math.min(H - 1, y2 + r + 1) * W + x3] - b[Math.max(0, y2 - r) * W + x3];
        }
      }
    }
    return a;
  }

  function draw(canvas) {
    if (canvas.dataset.smpDrawn) return;
    canvas.dataset.smpDrawn = '1';
    var W = 600, H = 368, S = W / 1800; // composition authored on an 1800 x 1100 grid
    canvas.width = W; canvas.height = H;
    var rand = rng(parseInt(canvas.dataset.seed || '5', 10));

    // 1) Cumulus density: clusters of soft puffs, squashed below their centre (flat bellies)
    var dens = new Float32Array(W * H);
    function cluster(cx, cy, sx, sy, n, rmin, rmax) {
      for (var k = 0; k < n; k++) {
        var px = (cx + normal(rand) * sx) * S, py = (cy + normal(rand) * sy) * S;
        var sig = (rmin + rand() * (rmax - rmin)) * 0.55 * S;
        var inv = 1 / (2 * sig * sig), reach = sig * 3.2;
        var x0 = Math.max(0, Math.floor(px - reach)), x1 = Math.min(W - 1, Math.ceil(px + reach));
        var y0 = Math.max(0, Math.floor(py - reach)), y1 = Math.min(H - 1, Math.ceil(py + reach / 1.6));
        for (var y = y0; y <= y1; y++) {
          var dy = y - py; if (dy > 0) dy *= 1.6;
          var dy2 = dy * dy, row = y * W;
          for (var x = x0; x <= x1; x++) {
            var dx = x - px;
            dens[row + x] += Math.exp(-(dx * dx + dy2) * inv);
          }
        }
      }
    }
    cluster(1350, 820, 330, 110, 70, 70, 190);
    cluster(380, 900, 260, 80, 45, 60, 150);
    cluster(800, 520, 240, 60, 28, 40, 110);
    cluster(260, 300, 140, 40, 14, 30, 80);
    cluster(1500, 260, 160, 45, 14, 30, 85);

    // 2) Ragged edges + coverage
    var edge = fbm(W, H, [90, 45, 22, 11, 6], 0.55, S, rand);
    var fine = fbm(W, H, [14, 7, 4], 0.7, S, rand);
    var cov = new Float32Array(W * H);
    for (var i = 0; i < cov.length; i++) {
      var d = dens[i] + 1.5 * (edge[i] - 0.5) + 0.35 * (fine[i] - 0.5);
      var c = Math.min(1, Math.max(0, (d - 0.62) / 0.38));
      cov[i] = Math.pow(c, 0.9);
    }
    cov = blur(cov, W, H, 1);

    // 3) Top-lit shading: bright where there's less cloud above, grey bellies
    var clipped = new Float32Array(W * H);
    for (var j = 0; j < clipped.length; j++) clipped[j] = Math.min(3, Math.max(0, dens[j]));
    var lit = blur(clipped, W, H, 10 * S);
    var inner = fbm(W, H, [60, 30, 15], 0.6, S, rand);
    var shift = Math.round(28 * S);

    var ctx = canvas.getContext('2d');
    var img = ctx.createImageData(W, H);
    var out = img.data;
    for (var y = 0; y < H; y++) {
      var t = y / H;
      var skyR = 24 + 46 * t, skyG = 120 + 44 * t, skyB = 196 + 30 * t;
      for (var x = 0; x < W; x++) {
        var k = y * W + x, p = k * 4;
        var above = y - shift >= 0 ? lit[k - shift * W] : 0;
        var shade = Math.max(-1, Math.min(1, (lit[k] - above) * 1.1));
        var tone = 0.8 + 0.35 * shade + 0.34 * (inner[k] - 0.5) - 0.1 * Math.min(2, Math.max(0, lit[k] - 1.5));
        tone = Math.max(0.5, Math.min(1, tone));
        var q = (tone - 0.5) / 0.5;
        var cr = 140 + 110 * q, cg = 160 + 91 * q, cb = 184 + 69 * q;
        var cv = cov[k];
        var grain = normal(rand) * 7;
        out[p]     = skyR * (1 - cv) + cr * cv + grain;
        out[p + 1] = skyG * (1 - cv) + cg * cv + grain;
        out[p + 2] = skyB * (1 - cv) + cb * cv + grain;
        out[p + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
    canvas.classList.add('is-ready');
  }

  // Fine film grain tile (screen resolution) layered over the sky via CSS (--smp-grain)
  function grainTile() {
    var root = document.documentElement;
    if (root.style.getPropertyValue('--smp-grain')) return;
    var c = document.createElement('canvas'); c.width = c.height = 160;
    var g = c.getContext('2d'), d = g.createImageData(160, 160), rand = rng(9);
    for (var i = 0; i < d.data.length; i += 4) {
      var v = rand() * 255;
      d.data[i] = d.data[i + 1] = d.data[i + 2] = v;
      d.data[i + 3] = 38;
    }
    g.putImageData(d, 0, 0);
    root.style.setProperty('--smp-grain', 'url(' + c.toDataURL('image/png') + ')');
  }

  function init() {
    var canvases = document.querySelectorAll('canvas[data-smp-clouds]');
    if (!canvases.length) return;
    grainTile();
    // First sky (hero) paints right away; the rest paint when they approach the viewport
    var list = Array.prototype.slice.call(canvases);
    var first = list.shift();
    (window.requestAnimationFrame || setTimeout)(function () { draw(first); });
    if (!list.length) return;
    if (!('IntersectionObserver' in window)) { setTimeout(function () { list.forEach(draw); }, 300); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { draw(e.target); io.unobserve(e.target); }
      });
    }, { rootMargin: '600px 0px' });
    list.forEach(function (c) { io.observe(c); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
